import isArray from 'lodash/isArray'
import { usePrintTemplate } from './usePrintTemplate'
import qz from 'qz-tray'
import { useToast } from '../useToast'
import { useError } from '../useError'
import type { QzConsumerProps, QzOption } from './useQz'
import { useState } from 'react'
import { useDeepCompareEffect } from '../useDeepCompareEffect'
import includes from 'lodash/includes'
import { humanize } from '@ztechapps/common.utils'

type UseQzPrintProps = {
  type: string
} & QzConsumerProps

export type PrinterOptions = {
  bounds: {
    x: number
    y: number
    width: number
    height: number
  }
  margins: {
    top: number
    left: number
    bottom: number
    right: number
  }
  orientation: 'landscape' | 'portrait' | 'reverse-landscape'
  printerName: string
  qzOption?: QzOption
  rendering: {
    pageWidth: number
    pageHeight: number
  }
  size: {
    width: number
    height: number
  }
  units: 'in' | 'mm' | 'cm'
}

export type PrintOptions = {
  file?: unknown
  flavor?: 'base64' | 'file' | 'hex' | 'plain' | 'xml'
  copies?: number
  printerName?: string
  printerOptions?: PrinterOptions
  qzOption?: QzOption
}

export type OnPrintFunction = (options?: PrintOptions) => Promise<void>

type UseQzPrintResult = {
  onPrint: OnPrintFunction
  printOptions: PrintOptions
  printerName: string
  printerOptions: Record<string, any>
}
/**
 * Prints a file using QZ Tray.
 * THe printer settings should is available on the printerTemplate.
 * @param type - this type is used to find the print template
 * @param useQz - the qz function that is used to initialize the qz tray
 */
export const useQzPrint = ({ type, useQz }: UseQzPrintProps): UseQzPrintResult => {
  const { template, printerName, printerOptions, qzOption } = usePrintTemplate({ type })
  const [printOptions, setPrintOptions] = useState({
    printerOptions,
    printerName,
  } as PrintOptions)

  useDeepCompareEffect(() => {
    if (template) setPrintOptions({ printerName, printerOptions })
  }, [template])

  const { onError } = useError()
  const { showToast } = useToast()
  const { initialize } = useQz()

  const onPrint = async (options?: PrintOptions) => {
    //merge the function options and default options from the template
    const mergeOptions: PrintOptions = {
      printerName,
      printerOptions,
      flavor: 'file',
      copies: 1,
      qzOption,
      ...options,
    }

    setPrintOptions(mergeOptions)

    try {
      if (!template) {
        throw new Error(
          `There is no print template found for ${type}. Create a new print template for ${type} on settings screen`
        )
      }

      //this will do the initial connection too
      await initialize(mergeOptions.qzOption)

      let printer = await qz.printers.find(mergeOptions.printerName)

      if (printer?.length > 1) {
        printer = printer?.[0]
      }

      if (!printer) {
        showToast({
          type: 'danger',
          message: `Cannot find any printer installed.`,
        })
        return
      }

      const config = await qz.configs.create(printer, { ...mergeOptions.printerOptions, copies: mergeOptions.copies })

      const data = []
      const mapper = (item) => {
        return {
          type: 'pixel',
          format: 'pdf',
          flavor: mergeOptions.flavor,
          data: item,
          options: {
            pageWidth: mergeOptions?.printerOptions?.rendering?.pageWidth,
            pageHeight: mergeOptions?.printerOptions?.rendering?.pageHeight,
          },
        }
      }
      if (isArray(mergeOptions.file)) {
        data.push(...mergeOptions.file.map(mapper))
      } else {
        data.push(mapper(mergeOptions.file))
      }

      showToast({
        type: 'info',
        message: `Started printing '${humanize(type)}'. Check your printer for the progress.`,
      })
      await qz.print(config, data)
    } catch (err) {
      //the the error message to be more specific
      if (includes(err.message, `Cannot read properties of null (reading 'sendData')`)) {
        onError({ message: 'You need to setup your printing template account first.' })
      } else if (includes(err.message, `No commands found to send to the printer`)) {
        onError({
          message:
            'Cannot access the file to be printed. Make sure is available via remote QZ Printer. On local development, localhost cannot be accessed.',
        })
      } else {
        onError(err)
      }
    } finally {
      if (qz.websocket.isActive()) await qz.websocket.disconnect()
    }
  }

  return { onPrint, printOptions, printerName: printOptions.printerName, printerOptions: printOptions.printerOptions }
}
